import {
    Autocomplete,
    Box,
    Card,
    CardContent,
    Chip,
    Grid,
    Paper,
    Typography,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    TextField,
    InputAdornment,
    Button,
  } from "@mui/material";
  import dayjs from "dayjs";
  import { useEffect, useRef, useState, useMemo } from "react";
  import Title from "../components/PageTitle";
  import apiService from "../Services/api.service";
  import { Assignment, HourglassBottom, CheckCircle, Search } from "@mui/icons-material"; 
  import { useNavigate } from "react-router-dom";

  const Support = () => {
    const [tickets, setTickets] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
  
    const [recentlyMovedId, setRecentlyMovedId] = useState(null);
    const [noteDrafts, setNoteDrafts] = useState({});
    const [addingNoteId, setAddingNoteId] = useState(null);
    const [createTicketOpen, setCreateTicketOpen] = useState(false);
    const [creatingTicket, setCreatingTicket] = useState(false);
    const [doctorOptions, setDoctorOptions] = useState([]);
    const [clinicOptions, setClinicOptions] = useState([]);
    const [newTicket, setNewTicket] = useState({
      doctor: null,
      clinic: null,
      subject: "",
      description: "",
      priority: "medium",
      category: "general",
    });
    const navigate = useNavigate();
    // No "All" option — always exactly one status selected, so there's
    // nothing to collapse/expand between sections any more.
    const [statusFilter, setStatusFilter] = useState("open");
    const [priorityFilter, setPriorityFilter] = useState("all");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [statusCounts, setStatusCounts] = useState({ open: 0, "in-progress": 0, closed: 0 });
    const limit = 40; // tickets per page
    const token = localStorage.getItem("token");
    const cardRefs = useRef({});

    useEffect(() => {
      fetchTickets();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, searchQuery, statusFilter, priorityFilter, categoryFilter]);

    const fetchTickets = async () => {
      try {
        setLoading(true);
        const response = await apiService.fetchSupportTickets(
          page,
          limit,
          searchQuery,
          token,
          statusFilter,
          priorityFilter === "all" ? "" : priorityFilter,
          categoryFilter === "all" ? "" : categoryFilter
        );
        if (response?.tickets) {
          setTickets(response.tickets);
          setTotalPages(response.pagination?.totalPages || 1);
          setStatusCounts(response.statusCounts || { open: 0, "in-progress": 0, closed: 0 });
        }
      } catch (err) {
        console.error("Failed to fetch tickets:", err);
      } finally {
        setLoading(false);
      }
    };

    const handleStatusChange = async (ticketId, newStatus) => {
      try {
        await apiService.changeTicketStatus(ticketId, newStatus, token);
        setTickets((prev) =>
          prev.map((t) => (t._id === ticketId ? { ...t, status: newStatus } : t))
        );

        // Switch the filter to wherever the ticket just moved, since
        // otherwise it would just vanish from the current filtered view,
        // then scroll its card into view and highlight it.
        applyStatusFilter(newStatus);
        setTimeout(() => {
          cardRefs.current[ticketId]?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 350); // let the new page render first

        setRecentlyMovedId(ticketId);
        setTimeout(() => {
          setRecentlyMovedId((current) => (current === ticketId ? null : current));
        }, 8000);
      } catch (err) {
        console.error("Failed to update status:", err);
      }
    };

    const openCreateTicket = () => {
      setNewTicket({
        doctor: null,
        clinic: null,
        subject: "",
        description: "",
        priority: "medium",
        category: "general",
      });
      setDoctorOptions([]);
      setClinicOptions([]);
      setCreateTicketOpen(true);
    };

    const handleDoctorSearch = async (keyword, clinicId = "") => {
      try {
        const res = await apiService.searchDoctorsForTicket(keyword, clinicId, token);
        setDoctorOptions(res?.doctors || []);
      } catch (err) {
        console.error("Failed to search doctors:", err);
      }
    };

    const handleClinicSearch = async (keyword) => {
      try {
        const res = await apiService.fetchDoctorAndClinics("", keyword, token);
        setClinicOptions(res?.data?.clinics || []);
      } catch (err) {
        console.error("Failed to search clinics:", err);
      }
    };

    const handleCreateTicket = async () => {
      if (!newTicket.doctor) {
        alert("Select a doctor");
        return;
      }
      if (!newTicket.subject.trim()) {
        alert("Subject is required");
        return;
      }
      setCreatingTicket(true);
      try {
        const res = await apiService.createTicket(
          {
            doctor: newTicket.doctor._id,
            clinic: newTicket.clinic?._id,
            subject: newTicket.subject.trim(),
            description: newTicket.description.trim(),
            priority: newTicket.priority,
            category: newTicket.category,
          },
          token
        );
        if (res.success) {
          setCreateTicketOpen(false);
          fetchTickets();
        }
      } catch (err) {
        console.error("Failed to create ticket:", err);
      } finally {
        setCreatingTicket(false);
      }
    };


    const handleAddNote = async (ticketId) => {
      const text = (noteDrafts[ticketId] || "").trim();
      if (!text) return;
      setAddingNoteId(ticketId);
      try {
        const res = await apiService.addTicketNote(ticketId, text, token);
        if (res.success) {
          setTickets((prev) =>
            prev.map((t) => (t._id === ticketId ? res.ticket : t))
          );
          setNoteDrafts((prev) => ({ ...prev, [ticketId]: "" }));
        }
      } catch (err) {
        console.error("Failed to add note:", err);
      } finally {
        setAddingNoteId(null);
      }
    };

    const statusOptions = ["open", "in-progress", "closed"];

    const statusMeta = {
      open: { label: "Open", color: "success", icon: <Assignment /> },
      "in-progress": { label: "In Progress", color: "warning", icon: <HourglassBottom /> },
      closed: { label: "Closed", color: "default", icon: <CheckCircle /> },
    };
  
    const groupedTickets = useMemo(() => {
      const acc = { open: [], "in-progress": [], closed: [] };
      tickets.forEach((t) => {
        acc[t.status]?.push(t);
      });
      return acc;
    }, [tickets]);
  
    const applyStatusFilter = (status) => {
      setStatusFilter(status);
      setPage(1); // a different filter has its own page count, so start fresh
    };

    const applyPriorityFilter = (priority) => {
      setPriorityFilter(priority);
      setPage(1);
    };

    const applyCategoryFilter = (category) => {
      setCategoryFilter(category);
      setPage(1);
    };

    const hasActiveFilters =
      statusFilter !== "open" || priorityFilter !== "all" || categoryFilter !== "all" || searchQuery.trim() !== "";

    const clearAllFilters = () => {
      setStatusFilter("open");
      setPriorityFilter("all");
      setCategoryFilter("all");
      setSearchQuery("");
      setPage(1);
    };

    const priorityOptions = ["low", "medium", "high"];
    const priorityMeta = {
      low: { label: "Low", color: "success" },
      medium: { label: "Medium", color: "warning" },
      high: { label: "High", color: "error" },
    };
    const categoryOptions = ["technical", "billing", "general", "other"];
    const categoryLabel = { technical: "Technical", billing: "Billing", general: "General", other: "Other" };
  
 
    return (
      <Paper
        sx={{
          p: 4,
          borderRadius: 4, 
          fontFamily: "'Inter', sans-serif",
        }}
      >
        <Title
          text="Support Tickets"
          showActions={true}
          actions={[
            {
              label: "Create Ticket",
              onClick: openCreateTicket,
            },
          ]}
        />
  
        {/* 🔍 Search Field */}
        <Box sx={{ mb: 3, mt: 2 }}>
          <TextField
            fullWidth
            placeholder="Search by Doctor or Clinic"
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1); // reset to first page when searching
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ 
              borderRadius: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />
        </Box>

        {/* Status / Priority / Category filters — all in one row */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3, alignItems: "center" }}>
          {statusOptions.map((status) => (
            <Chip
              key={status}
              icon={statusMeta[status].icon}
              label={`${statusMeta[status].label} (${statusCounts[status] || 0})`}
              color={statusFilter === status ? statusMeta[status].color : "default"}
              variant={statusFilter === status ? "filled" : "outlined"}
              onClick={() => applyStatusFilter(status)}
              sx={{ fontWeight: 600, cursor: "pointer" }}
            />
          ))}

          <FormControl size="small" sx={{ minWidth: 140, ml: 1 }}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={priorityFilter}
              label="Priority"
              onChange={(e) => applyPriorityFilter(e.target.value)}
            >
              <MenuItem value="all">All priorities</MenuItem>
              {priorityOptions.map((p) => (
                <MenuItem key={p} value={p}>{priorityMeta[p].label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              label="Category"
              onChange={(e) => applyCategoryFilter(e.target.value)}
            >
              <MenuItem value="all">All categories</MenuItem>
              {categoryOptions.map((c) => (
                <MenuItem key={c} value={c}>{categoryLabel[c]}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {hasActiveFilters && (
            <Button size="small" onClick={clearAllFilters} sx={{ textTransform: "none" }}>
              Clear filters
            </Button>
          )}
        </Box>

        {loading ? (
          <Typography variant="body2" sx={{ textAlign: "center", mt: 4 }}>
            Loading tickets...
          </Typography>
        ) : (
          [statusFilter].map((status) => {
            const ticketsByStatus = groupedTickets[status] || [];
            const sectionTotal = statusCounts[status] || 0;

            return (
              <Box key={status} sx={{ mt: 5 }}>
                {/* Section Header */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2.5,
                    py: 1,
                    borderRadius: 3,
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                  }}
                >
                  {statusMeta[status].icon}
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {statusMeta[status].label} Tickets
                  </Typography>
                  <Chip
                    label={sectionTotal}
                    color={statusMeta[status].color}
                    size="small"
                  />
                </Box>

                <Box sx={{ mt: 2 }}>
                  {ticketsByStatus.length > 0 ? (
                    <Grid container spacing={3} sx={{ mt: 2 }}>
                      {ticketsByStatus.map((ticket) => (
                        <Grid item xs={12} sm={6} md={4} key={ticket._id}>
                          <Card
                            ref={(el) => {
                              cardRefs.current[ticket._id] = el;
                            }}
                            variant="outlined"
                            // onClick={() => navigate(`/dashboard/support/chat/${ticket._id}`, {
                            //   state: {
                            //     ticketId: ticket._id,
                            //     doctor:ticket.doctor.name,
                            //     clinic: ticket.doctor.clinics?.[0]?.name || "NA"
                            //
                            //   },
                            // })}
                            sx={{
                              height: "100%",
                              borderRadius: 3,
                              boxShadow: "0px 2px 10px rgba(0,0,0,0.05)",
                              transition: "background-color 1.5s ease, box-shadow 0.25s, transform 0.25s",
                              bgcolor:
                                recentlyMovedId === ticket._id
                                  ? "rgba(135, 78, 246, 0.15)"
                                  : "transparent",
                              border:
                                recentlyMovedId === ticket._id
                                  ? "1px solid #874EF6"
                                  : "1px solid transparent",
                              "&:hover": {
                                transform: "translateY(-4px)",
                                boxShadow: "0px 4px 14px rgba(0,0,0,0.12)",
                              },
                            }}
                          >
                            <CardContent
                              sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                            >
                              {/* Status + Priority */}
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 1,
                                }}
                              >
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                  <InputLabel>Status</InputLabel>
                                  <Select
                                    value={ticket.status}
                                    label="Status"
                                    onChange={(e) =>
                                      handleStatusChange(ticket._id, e.target.value)
                                    }
                                  >
                                    {statusOptions.map((opt) => (
                                      <MenuItem key={opt} value={opt}>
                                        {opt.replace("-", " ")}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
  
                                <Chip
                                  label={(priorityMeta[ticket.priority] || priorityMeta.medium).label.toUpperCase()}
                                  color={(priorityMeta[ticket.priority] || priorityMeta.medium).color}
                                  size="small"
                                  sx={{ fontWeight: 500 }}
                                />
                              </Box>
  
                              {/* Subject */}
                              <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 700, mt: 1 }}
                              >
                                {ticket.subject}
                              </Typography>
  
                              {/* Description */}
                              <Typography
                                variant="body2"
                                sx={{ 
                                  mb: 1,
                                  display: "-webkit-box",
                                  WebkitLineClamp: 3,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                   color:"text.secondary"
                                }}
                              >
                                {ticket.description}
                              </Typography>
  
                              <Divider sx={{ my: 1 }} />

                              {/* Doctor & Clinic — one compact line instead of two */}
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {ticket.doctor?.name || "N/A"}
                                <Box component="span" sx={{ fontWeight: 400, color: "text.secondary" }}>
                                  {ticket.doctor?.clinics?.[0]?.name ? ` — ${ticket.doctor.clinics[0].name}` : ""}
                                </Box>
                              </Typography>

                              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75, mt: 1 }}>
                                <Chip
                                  label={
                                    ticket.source === "dashboard"
                                      ? `Created by ${ticket.createdByName || "ops"}`
                                      : "From kiosk"
                                  }
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: "0.7rem" }}
                                />
                                <Chip
                                  label={categoryLabel[ticket.category] || ticket.category}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: "0.7rem" }}
                                />
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                {ticket.ticketId} · Last message:{" "}
                                {dayjs(ticket.lastMessageAt).format("DD MMM YYYY, hh:mm A")}
                              </Typography>

                            
                              {ticket.status === "in-progress" && (
                                <Box sx={{ mt: 1.5 }}>
                                  <Divider sx={{ mb: 1 }} />
                                  <Button
                                    variant="contained"
                                    size="small"
                                    sx={{ textTransform: "none", borderRadius: 2 }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(
                                        `/dashboard/support/ticket/${ticket._id}/logs`
                                      );
                                    }}
                                  >
                                    {`Show logs${
                                      (ticket.internalNotes || []).length
                                        ? ` (${ticket.internalNotes.length})`
                                        : ""
                                    }`}
                                  </Button>

                                  <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      placeholder="Log a step/update..."
                                      value={noteDrafts[ticket._id] || ""}
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={(e) =>
                                        setNoteDrafts((prev) => ({
                                          ...prev,
                                          [ticket._id]: e.target.value,
                                        }))
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          e.stopPropagation();
                                          handleAddNote(ticket._id);
                                        }
                                      }}
                                    />
                                    <Button
                                      variant="contained"
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleAddNote(ticket._id);
                                      }}
                                      disabled={
                                        addingNoteId === ticket._id ||
                                        !(noteDrafts[ticket._id] || "").trim()
                                      }
                                    >
                                      Add
                                    </Button>
                                  </Box>
                                </Box>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 2, mt: 1 }}
                    >
                      No {status.replace("-", " ")} tickets found.
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })
        )}

        {/* 📄 Pagination Controls */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mt: 5,
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </Button>
          <Typography variant="body2">
            Page {page} of {totalPages}
          </Typography>
          <Button
            variant="outlined"
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </Box>

        {/* Create Ticket Dialog */}
        <Dialog
          open={createTicketOpen}
          onClose={() => setCreateTicketOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Create Ticket</DialogTitle>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
              <Autocomplete
                options={doctorOptions}
                value={newTicket.doctor}
                getOptionLabel={(option) =>
                  option.clinics?.length
                    ? `${option.name} — ${option.clinics.map((c) => c.name).join(", ")}`
                    : option.name || ""
                }
                isOptionEqualToValue={(option, value) => option._id === value._id}
                onChange={(e, value) => {
                  const clinics = value?.clinics || [];
                  setNewTicket((prev) => ({
                    ...prev,
                    doctor: value,
                    clinic: clinics.length === 1 ? clinics[0] : null,
                  }));
                  setClinicOptions(clinics);
                }}
                onInputChange={(e, value) =>
                  handleDoctorSearch(value, newTicket.clinic?._id || "")
                }
                renderInput={(params) => (
                  <TextField {...params} label="Doctor" placeholder="Search doctor by name" />
                )}
              />
              <Autocomplete
                options={clinicOptions}
                value={newTicket.clinic}
                getOptionLabel={(option) => option.name || ""}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                onChange={(e, value) => {
                  setNewTicket((prev) => ({ ...prev, clinic: value }));
                  handleDoctorSearch("", value?._id || "");
                }}
                onInputChange={(e, value) => handleClinicSearch(value)}
                renderInput={(params) => (
                  <TextField {...params} label="Clinic" placeholder="Search clinic by name" />
                )}
              />
              <TextField
                label="Subject"
                value={newTicket.subject}
                onChange={(e) =>
                  setNewTicket((prev) => ({ ...prev, subject: e.target.value }))
                }
                fullWidth
              />
              <TextField
                label="Description"
                value={newTicket.description}
                onChange={(e) =>
                  setNewTicket((prev) => ({ ...prev, description: e.target.value }))
                }
                multiline
                minRows={3}
                fullWidth
              />
              <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
                <TextField
                  select
                  label="Priority"
                  value={newTicket.priority}
                  onChange={(e) =>
                    setNewTicket((prev) => ({ ...prev, priority: e.target.value }))
                  }
                  fullWidth
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                </TextField>
                <TextField
                  select
                  label="Category"
                  value={newTicket.category}
                  onChange={(e) =>
                    setNewTicket((prev) => ({ ...prev, category: e.target.value }))
                  }
                  fullWidth
                >
                  <MenuItem value="technical">Technical</MenuItem>
                  <MenuItem value="billing">Billing</MenuItem>
                  <MenuItem value="general">General</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </TextField>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateTicketOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleCreateTicket}
              disabled={creatingTicket}
            >
              Create
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    );
  };
  
  export default Support;
  